import {
  type AnyObject,
  mixed,
  type ObjectSchema,
  object,
  type StringSchema,
  string,
} from "yup";
import type { BabyEntryPage, BabyQuote } from "@/api_client";
import { DateLimits } from "@/components/products/baby/utils/types";
import { getErrorMessage } from "@/utils/errorMessage";
import { AgeValidator } from "@/validators/shared/ageValidators";
import {
  isWithinDateRange,
  WithinDateRangeError,
} from "@/validators/shared/dateValidator";
import { FinnishSSN } from "@/validators/shared/ssnValidator";

const validateFemaleSSN = (val: string) => {
  const ssn = new FinnishSSN(val);
  return ssn.validateFemaleSSN();
};
const validateCorrectAge = (val: string) => {
  const ssn = new AgeValidator(val);
  return ssn.isAtMost45;
};

type BabyEntryFormValues = Pick<
  BabyQuote,
  | "productType"
  | "insuredParty"
  | "policyHolderSsn"
  | "babyMother"
  | "expectedDateOfBirth"
>;

const BabyMotherValidationSchema = (
  schema: StringSchema<string, AnyObject, undefined, "">,
  content?: BabyEntryPage | null,
) => {
  return schema
    .test(
      "wrong-ssn",
      getErrorMessage(
        content?.entryPageCommon?.ssnQuestion?.validationMessages?.value,
        "wrong-female-ssn",
      ),
      validateFemaleSSN,
    )
    .test(
      "too-old",
      getErrorMessage(
        content?.babyMotherSsn?.validationMessages?.value,
        "too-old",
      ),
      validateCorrectAge,
    );
};

export const BabyEntryValidationSchema = (
  content?: BabyEntryPage | null,
): ObjectSchema<BabyEntryFormValues> =>
  object({
    productType: string().oneOf(["baby"]).required(),
    insuredParty: mixed<"Mother" | "PolicyHolder">()
      .required(
        getErrorMessage(
          content?.insuredParty?.validationMessages?.value,
          "required",
        ),
      )
      .oneOf(["Mother", "PolicyHolder"]),
    policyHolderSsn: string()
      .required(
        getErrorMessage(
          content?.entryPageCommon?.ssnQuestion?.validationMessages?.value,
          "required",
        ),
      )
      .when("insuredParty", {
        is: "Mother",
        then: (schema) =>
          schema.test(
            "wrong-ssn",
            getErrorMessage(
              content?.entryPageCommon?.ssnQuestion?.validationMessages?.value,
              "wrong-ssn",
            ),
            (val) => {
              const ssn = new FinnishSSN(val);
              return ssn.validateSSN();
            },
          ),
        otherwise: (schema) => BabyMotherValidationSchema(schema, content),
      }),
    babyMother: object({ ssn: string() }).when(
      "insuredParty",
      (insuredParty: unknown, schema) => {
        const value = Array.isArray(insuredParty)
          ? insuredParty[0]
          : insuredParty;
        if (value === "Mother") {
          return schema.shape({
            ssn: BabyMotherValidationSchema(
              string()
                .required(
                  getErrorMessage(
                    content?.babyMotherSsn?.validationMessages?.value,
                    "required",
                  ),
                )
                .test(
                  "duplicate-ssn",
                  getErrorMessage(
                    content?.babyMotherSsn?.validationMessages?.value,
                    "duplicate-ssn",
                  ),
                  (value, ctx) => {
                    return (ctx.from ?? [])[1].value.policyHolderSsn !== value;
                  },
                ),
              content,
            ),
          });
        }
        return schema.shape({ ssn: string().notRequired() });
      },
    ),
    expectedDateOfBirth: string()
      .required(
        getErrorMessage(
          content?.expectedDateOfBirth?.validationMessages?.value,
          "required",
        ),
      )
      .test("single-date-validation", "", (value, context) => {
        const dateError = isWithinDateRange(
          value,
          DateLimits.MIN,
          DateLimits.MAX,
        );
        switch (dateError) {
          case WithinDateRangeError.invalidDate:
            return context.createError({
              message: getErrorMessage(
                content?.expectedDateOfBirth?.validationMessages?.value,
                "required",
              ),
            });
          case WithinDateRangeError.inThePast:
            return context.createError({
              message: getErrorMessage(
                content?.expectedDateOfBirth?.validationMessages?.value,
                "is-not-future",
              ),
            });
          case WithinDateRangeError.tooSmall:
            return context.createError({
              message: getErrorMessage(
                content?.expectedDateOfBirth?.validationMessages?.value,
                "less-then",
              ),
            });
          case WithinDateRangeError.tooLarge:
            return context.createError({
              message: getErrorMessage(
                content?.expectedDateOfBirth?.validationMessages?.value,
                "more-then",
              ),
            });
        }
        return true;
      }),
  });
